class SearchStateManager {
  baseState = {

    // Structure and type cases
    // defaultState.key = data categories
    // defaultState.key.Array = supports multiple values
    // defaultState.key.String | Number = supports single value
    // defaultState.key.Boolean = supports single value
    // defaultState.key.Object = probably going too deep

    sort: 'best',
    license: 'all',
    orientation: [],
    editor: false,
    page: 1,
    image_style: [],
    num_people: [],
    people_age: [],
    people_composition: [],
    ethnicity: [],
  }

  defaultState = JSON.parse(JSON.stringify(this.baseState));

  omittedArray = [
    'content',
  ];

  constructor() {
    this.updateStateFromURL();
  }

  updateStateFromURL() {
    const URLParamStateObject = this.returnURLParamState();
    const currentRawState = this.getRawStateString();

    if (!currentRawState && URLParamStateObject) {
      // set up state from default + URL is none exists
      this.assignDefaultsWithParams(URLParamStateObject);
    } else if (currentRawState && URLParamStateObject) {
      // otherwise set state to current state + URL
      this.assignDefaultsWithParams({ ...this.getRawStateObject(), ...URLParamStateObject });
    } else if (!currentRawState && !URLParamStateObject) {
      // if we have no  current state, set to default
      this.assignDefaultsWithParams({});
    }
    // lastly (currentRawState && !URLParamStateObject)
    // we have current state but don't need to change it.
  }

  // only added once, sets the state on back/forward
  addPopStateListener() {
    window.addEventListener('popstate', () => {
      this.updateStateFromURL();
    });
  }

  // set default state, including params set by the URL
  assignDefaultsWithParams(stateObjectToMerge) {
    if (stateObjectToMerge) {
      this.saveStateToSession({ ...this.defaultState, ...stateObjectToMerge });
    } else {
      throw new Error('No state object was given.');
    }
  }

  // returns a state object from URL parameters, that differ from this.defaultState
  // NTH: Unit test this bad boi
  // NTH: Refactor and seperate responsibilities
  returnURLParamState() {
    const url = window.location.search;
    const query = url.substring(1);
    const URLParamObj = {};
    let URLDefaultParamState = null;

    // split along & and populate object
    query.split('&').forEach((part) => {
      const pieces = part.split('=');
      const key = pieces.shift();
      const value = pieces.join('=');

      URLParamObj[key] = decodeURIComponent(value);
    });

    // loop over URL parameters, to populate URLDefaultParamState objec
    const paramKeys = Object.keys(URLParamObj);
    for (let i = 0; i < paramKeys.length; i += 1) {
      const param = paramKeys[i];
      const splitParam = param.split('-');

      // if params are found in this.defaultState
      if (Object.hasOwnProperty.call(this.defaultState, splitParam[0])) {
        const paramType = Object.prototype.toString.call(this.defaultState[splitParam[0]]);
        if (URLDefaultParamState === null) URLDefaultParamState = {};

        // checking for 'flattened' params (ie license-free=true)
        if (splitParam.length > 1) {
          const [key, value] = splitParam; // destruct earlier?

          switch (paramType) {
            case '[object Array]':
              // setup param as array if first entry
              if (typeof URLDefaultParamState[key] === 'undefined') URLDefaultParamState[key] = [];
              URLDefaultParamState[key].push(value);
              break;

            default:
              URLDefaultParamState[key] = value;
              break;
          }
        } else {
          try {
            URLDefaultParamState[splitParam[0]] = JSON.parse(URLParamObj[param]);
          } catch {
            URLDefaultParamState[splitParam[0]] = URLParamObj[param];
          }
        }
      }
    }

    return URLDefaultParamState || false;
  }

  // override the default state for a manager's instance
  // NTH: Optionally allow this in the constructor
  setNewDefaultState(newDefault) {
    this.defaultState = newDefault;
    this.saveStateToSession(this.defaultState);
  }

  // override the default state for a manager's instance
  // NTH: Optionally allow this in the constructor
  setDefaultState() {
    this.defaultState = JSON.parse(JSON.stringify(this.baseState));;
    this.saveStateToSession(this.defaultState);
  }

  // return the state string from session storage
  getRawStateString() {
    const stateString = sessionStorage.getItem('ezSearchState');
    return stateString || false;
  }

  // save an object to ezSearchState as a string
  saveStateToSession(completeState) {
    const completeStateString = JSON.stringify(completeState);
    sessionStorage.setItem('ezSearchState', completeStateString);
  }

  // return the default state object
  getDefaultStateObject() {
    return this.defaultState;
  }

  // return the parsed state object
  getRawStateObject() {
    const state = JSON.parse(this.getRawStateString());
    return { ...state };
  }

  // return array of param keys accounting for type
  // ie ['orientation-vertical', 'editor']
  getRawParamKeys() {
    const state = this.getRawStateObject();
    return this.flattenParamKeys(state);
  }

  // return array of param keys accounting for type
  // with defaults removed
  getParamKeys() {
    const state = this.getState();
    return this.flattenParamKeys(state);
  }

  // return array of param keys
  flattenParamKeys(state) {
    const stateKeys = Object.keys(state);
    const paramKeys = [];

    stateKeys.forEach((key) => {
      switch (Object.prototype.toString.call(state[key])) {
        case '[object Boolean]':
          if (state[key] === true) paramKeys.push(`${key}`);
          break;

        case '[object Number]':
          if (state[key] !== this.defaultState[key]) paramKeys.push(`${key}`);
          break;

        case '[object Array]':
          if (state[key].length > 0) {
            state[key].forEach((value) => {
              paramKeys.push(`${key}-${value}`);
            });
          }
          break;

        case '[object String]':
          paramKeys.push(`${key}-${state[key]}`);
          break;

        default:
          break;
      }
    });

    return paramKeys;
  }

  // format array into a flat query string by presence
  generateFlatQueryString(paramsArray) {
    const state = this.getRawStateObject();
    let queryString = '';
    let i = 0;

    paramsArray.forEach((param) => {
      if (typeof state[param] === 'number') {
        queryString += `${i === 0 ? '?' : '&'}${encodeURIComponent(param)}=${state[param]}`;
      } else {
        queryString += `${i === 0 ? '?' : '&'}${encodeURIComponent(param)}=true`;
      }

      i += 1;
    });

    return queryString;
  }

  // get query string for all params
  getRawFlatQueryString() {
    const params = this.getRawParamKeys();
    return this.generateFlatQueryString(params);
  }

  // get query string for all params with defaults removed
  getFlatQueryString() {
    const params = this.getParamKeys();
    return this.generateFlatQueryString(params);
  }

  // return state object with defaults removed
  getState() {
    const state = this.getRawStateObject();
    const cleanedState = this.filterDefaults(state);

    return { ...cleanedState };
  }

  // loop over and remove any properties matching the default state
  filterDefaults(state) {
    const filteredState = {};
    const stateKeys = Object.keys(state);

    stateKeys.forEach((key) => {
      if (!this.deepCompare(state[key], this.defaultState[key])) {
        filteredState[key] = state[key];
      }
    });

    return filteredState;
  }

  // compare using json stringify
  deepCompare(valueOne, valueTwo) {
    return JSON.stringify(valueOne) === JSON.stringify(valueTwo);
  }

  // check if key is included in the omitted keys array
  omittedFromState(key) {
    return this.omittedArray.includes(key);
  }

  // replace a matching value in state
  updateStateValue(key, value) {
    const state = this.getRawStateObject();
    const newState = state;

    if (typeof state[key] === 'undefined') {
      throw new Error('State key does not exist');
    }

    if (typeof state[key] !== typeof value && typeof value !== 'boolean') {
      throw new Error(`New value typeof does not match current state. typeof state: ${typeof state[key]}, typeof value: ${typeof value}`);
    }

    newState[key] = value;
    if (key !== 'page') {
      newState.page = this.defaultState.page; // reset page status on change
    }
    this.saveStateToSession(newState);
  }

  // update a value in the state
  updateParamKey(paramKey) {
    const state = this.getRawStateObject();

    if (typeof state[paramKey] === 'boolean') {
      this.updateStateValue(paramKey, true);
      return; // this is a little gross
    }

    if (paramKey.indexOf('-') === -1) {
      throw new Error('Param key cannot be split');
    }

    const [key, value] = paramKey.split('-');

    if (Object.prototype.toString.call(state[key]) === '[object Array]') {
      this.pushToStateArray(key, value);
    } else {
      this.updateStateValue(key, value);
    }
  }

  // remove or reset a value from state
  removeParamKey(paramKey) {
    const state = this.getRawStateObject();

    if (typeof state[paramKey] === 'boolean') {
      this.updateStateValue(paramKey, false);
      return; // also gross
    }

    if (paramKey.indexOf('-') === -1) {
      throw new Error('Param key cannot be split');
    }

    const [key, value] = paramKey.split('-');

    if (Object.prototype.toString.call(state[key]) === '[object Array]') {
      this.removeFromStateArray(key, value);
    } else {
      this.updateStateValue(key, this.defaultState[key]);
    }
  }

  // add value to state array
  removeFromStateArray(key, value) {
    const state = this.getRawStateObject();
    const newState = state;

    if (Object.prototype.toString.call(state[key]) !== '[object Array]') {
      throw new Error('State key is not an array');
    }

    if (!state[key].includes(value)) {
      throw new Error('Value does not exist in state');
    }

    newState[key] = state[key].filter((stateValue) => stateValue !== value);

    if (newState[key].length === 0) {
      newState[key] = this.defaultState[key];
    }

    if (key !== 'page') {
      newState.page = this.defaultState.page; // reset page status on change
    }
    this.saveStateToSession(newState);
  }

  // add value to state array
  pushToStateArray(key, value) {
    const state = this.getRawStateObject();
    const newState = state;

    if (typeof value !== 'string') {
      throw new Error('array values must be strings');
    }

    if (Object.prototype.toString.call(state[key]) !== '[object Array]') {
      throw new Error('State key is not an array');
    }

    if (state[key].includes(value)) {
      throw new Error('Value already exists in state');
    }

    if (this.deepCompare(state[key], this.defaultState[key])) {
      newState[key] = [];
    }

    newState[key].push(value);

    if (key !== 'page') {
      newState.page = this.defaultState.page; // reset page status on change
    }

    this.saveStateToSession(newState);
  }

  // reset state key to default settings
  resetKeyToDefault(key) {
    const state = this.getRawStateObject();
    const newState = state;

    if (!this.defaultState[key]) {
      throw Error('This key does not have a default');
    }

    newState[key] = this.defaultState[key];
    this.saveStateToSession(newState);
  }

  // reset state key to default settings
  resetStateToDefault() {
    this.saveStateToSession(this.defaultState);
  }
}

export default SearchStateManager;
